import React, { useState, useEffect } from 'react';
import { X, Hash, AlertCircle } from 'lucide-react';

const AjustarMatriculaModal = ({ isOpen, onClose, matricula, onConfirm }) => {
  const [numeroMatricula, setNumeroMatricula] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && matricula) {
      setNumeroMatricula(matricula.numeroMatricula || '');
      setError('');
    }
  }, [isOpen, matricula]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validações
    if (!numeroMatricula.trim()) {
      setError('Número de matrícula é obrigatório');
      return;
    }

    if (numeroMatricula === matricula.numeroMatricula) {
      setError('O novo número deve ser diferente do atual');
      return;
    }

    setLoading(true);

    try {
      await onConfirm(matricula.id, numeroMatricula.trim());
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao ajustar número de matrícula');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !matricula) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Hash className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Ajustar Matrícula</h2>
              <p className="text-sm text-gray-500 mt-1">
                {matricula.nomeAluno}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Número Atual */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número Atual
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
              {matricula.numeroMatricula}
            </div>
          </div>

          {/* Novo Número */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Novo Número <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={numeroMatricula}
              onChange={(e) => setNumeroMatricula(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Digite o novo número"
              maxLength={20}
              disabled={loading}
              autoFocus
            />
            <p className="text-xs text-gray-500 mt-1">
              Máximo de 20 caracteres
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 font-medium">Erro</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Aviso */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Atenção</p>
                <p className="mt-1">
                  Esta ação irá alterar permanentemente o número de matrícula.
                  O sistema verificará se o novo número já está em uso.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Ajustando...' : 'Ajustar Matrícula'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AjustarMatriculaModal;
